import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function EyeForDetailGame({ gameState, gamePlayers, user, socket, roomId }) {
  const { submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = useMemo(() => normalizedState || gameState || {}, [normalizedState, gameState]);
  const meId = user ? (user.socketId || user.userId) : null;
  const players = state.players || gamePlayers || [];
  const [myAnswer, setMyAnswer] = useState({ value: null, round: -1 });
  const [timeLeft, setTimeLeft] = useState(0);
  const rafRef = useRef(null);

  const sceneSrc = useMemo(() => {
    if (!state.sceneSvg) return null;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(state.sceneSvg)}`;
  }, [state.sceneSvg]);

  const currentRound = state.currentQuestionIndex ?? -1;
  const phase = state.phase || 'waiting';
  const timerTotal = phase === 'study'
    ? (state.studyTime || 15)
    : phase === 'question'
      ? (state.questionTime || 12)
      : phase === 'reveal'
        ? (state.reviewAnswerTime || 3)
        : 0;

  useEffect(() => {
    if (Array.isArray(state.answers)) return;
    if (!state.answers || typeof state.answers !== 'object' || !meId) {
      setMyAnswer({ value: null, round: currentRound });
      return;
    }
    if (Object.prototype.hasOwnProperty.call(state.answers, meId)) {
      setMyAnswer({ value: state.answers[meId], round: currentRound });
      return;
    }
    setMyAnswer({ value: null, round: currentRound });
  }, [state.answers, meId, currentRound]);

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (!timerTotal || !['study', 'question', 'reveal'].includes(phase) || state.finished) {
      setTimeLeft(0);
      return undefined;
    }
    const startTime = Date.now();
    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const remaining = Math.max(0, Math.ceil(timerTotal - elapsed));
      setTimeLeft(remaining);
      if (elapsed < timerTotal && !state.finished && phase === (state.phase || 'waiting')) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    setTimeLeft(timerTotal);
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [phase, timerTotal, state.finished, state.phase, currentRound]);

  const handleAnswer = (option) => {
    if (phase !== 'question' || state.finished || myAnswer.round === currentRound && myAnswer.value) return;
    setMyAnswer({ value: option, round: currentRound });
    submitAction({ type: 'answer', answer: option }).catch(() => {});
  };

  const renderScoreboard = () => (
    <div className="rounded-3xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/70">
      <div className="mb-3 text-xs font-semibold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400">Scores</div>
      <div className="space-y-2">
        {players.map((player) => {
          const id = player.socketId || player.userId || player.id;
          const score = state.scores && typeof state.scores === 'object' ? (state.scores[id] || 0) : 0;
          return (
            <div key={id} className="flex items-center justify-between rounded-2xl bg-slate-100/80 px-3 py-2 text-sm text-slate-700 dark:bg-slate-800/80 dark:text-slate-200">
              <span className="font-semibold">{player.username || id}</span>
              <span>{score}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderRevealResults = () => (
    <div className="rounded-3xl border border-slate-200 bg-white/85 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/75">
      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400">Question Review</div>
      <div className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">{state.currentQuestion}</div>
      <div className="mb-4 text-base text-emerald-700 dark:text-emerald-300">
        Correct answer: <span className="font-bold">{state.correctAnswer}</span>
      </div>
      <div className="space-y-2">
        {players.map((player) => {
          const id = player.socketId || player.userId || player.id;
          const answer = state.answers && typeof state.answers === 'object' ? state.answers[id] : null;
          const isCorrect = answer != null && String(answer) === String(state.correctAnswer);
          const award = state.roundAwards && typeof state.roundAwards === 'object' ? state.roundAwards[id] : null;
          return (
            <div key={id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
              <div>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{player.username || id}</span>
                <span className="ml-2 text-slate-600 dark:text-slate-300">{answer || 'No answer'}</span>
              </div>
              <div className={`font-semibold ${isCorrect ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                {isCorrect ? `Correct${award ? ` (+${award})` : ''}` : 'Incorrect'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const isHost = (() => {
    const host = players[0] || null;
    if (!host || !user) return false;
    return (host.socketId && host.socketId === user.socketId) || (host.userId && host.userId === user.userId);
  })();

  return (
    <div className="mx-auto mt-6 w-full max-w-6xl px-4 text-slate-900 dark:text-slate-100">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.8fr)]">
        <div className="space-y-5">
          <div className="rounded-[2rem] border border-slate-200 bg-white/85 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.10)] dark:border-slate-800 dark:bg-slate-950/75">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="mt-2 text-2xl font-black tracking-tight">{phase === 'study' ? 'Memorise the scene' : phase === 'question' ? 'What do you remember?' : phase === 'reveal' ? 'Answer review' : phase === 'finished' ? 'Final results' : 'Ready to play'}</div>
              </div>
              {timerTotal > 0 && ['study', 'question', 'reveal'].includes(phase) && (
                <div className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                  {timeLeft}s left
                </div>
              )}
            </div>

            {sceneSrc && phase !== 'question' && (
              <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-inner dark:border-slate-800 dark:bg-slate-900">
                <img src={sceneSrc} alt="Memory scene" className="block h-auto w-full" />
              </div>
            )}

            {phase === 'question' && (
              <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50/80 p-2 text-center dark:border-slate-700 dark:bg-slate-900/60">
                <div className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-400 dark:text-slate-500">Scene Hidden</div>
                <div className="mt-3 text-lg font-semibold text-slate-700 dark:text-slate-200">Answer from memory only.</div>
              </div>
            )}

            {phase === 'waiting' && (
              <div className="mt-6 rounded-[1.5rem] bg-amber-50 px-5 py-4 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
                <div className="font-semibold">Study the generated image, then answer the quiz.</div>
                <div className="mt-1 text-sm opacity-80">You will get one shared image and {state.questionCount || 5} multiple-choice questions about it.</div>
              </div>
            )}

            {phase === 'study' && (
              <div className="mt-5 text-sm text-slate-600 dark:text-slate-300">
                Focus on positions, colours, counts, and any shapes pointing in a direction.
              </div>
            )}

            {phase === 'question' && (
              <div className="mt-5 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-[0.32em] text-slate-500 dark:text-slate-400">
                  Question {(state.currentQuestionIndex || 0) + 1} of {state.questionCount || 5}
                </div>
                <div className="text-xl font-semibold text-slate-900 dark:text-slate-100">{state.currentQuestion}</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(state.choices || []).map((option) => {
                    const selected = myAnswer.round === currentRound && String(myAnswer.value) === String(option);
                    const locked = myAnswer.round === currentRound && myAnswer.value !== null;
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => handleAnswer(option)}
                        disabled={locked || state.finished}
                        className={`rounded-[1.35rem] border px-4 py-2 text-left text-base font-semibold transition ${selected
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-slate-200 bg-white text-slate-900 hover:border-blue-400 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-blue-500 dark:hover:bg-slate-700'} disabled:cursor-not-allowed disabled:opacity-80`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {phase === 'reveal' && <div className="mt-5">{renderRevealResults()}</div>}

           

            {state.countdownToStart && !state.started && isHost && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => submitAction({ type: 'start' })}
                  className="rounded-full bg-emerald-600 px-6 py-3 text-sm font-bold text-white transition hover:bg-emerald-500"
                >
                  Start Eye for Detail
                </button>
              </div>
            )}
          </div>
        </div>


      </div>
    </div>
  );
}

EyeForDetailGame.Guide = function EyeForDetailGuide() {
  return (
    <div>
      <h3 className="mb-2 font-bold">Eye for Detail</h3>
      <p>Study the generated scene, then answer multiple-choice questions about where shapes were, what colors they had, how many appeared, and which way they pointed.</p>
    </div>
  );
};

export default EyeForDetailGame;